import { householdIdSchema } from "@estate-platform/domain/ids";
import { locales } from "@estate-platform/i18n/runtime";
import { z } from "zod";

import type { ApiClient } from "./client.ts";
import type { Query } from "./query.ts";

const viewerRoles = ["owner", "manager", "staff", "resident", "vendor"] as const;
export type ViewerRole = (typeof viewerRoles)[number];

const membershipSchema = z
  .object({
    household_id: householdIdSchema,
    household_name: z.string().min(1),
    household_version: z.number().int().positive(),
    role: z.enum(viewerRoles),
    display_name: z.string().min(1).nullable(),
    household_language: z.enum(locales),
    casual: z.boolean(),
  })
  .transform((row) => ({
    householdId: row.household_id,
    householdName: row.household_name,
    householdVersion: row.household_version,
    role: row.role,
    displayName: row.display_name,
    householdLanguage: row.household_language,
    isCasual: row.casual,
  }));

export type Viewer = z.infer<typeof membershipSchema>;

export const viewerKey = ["household", "viewer"] as const;

export const viewerQuery = (client: ApiClient): Query<typeof viewerKey, Viewer | null> => ({
  queryKey: viewerKey,
  queryFn: async () => {
    const { data, error } = await client.db.rpc("viewer");
    if (error !== null) {
      throw new Error(error.message, { cause: error });
    }
    return z.array(membershipSchema).parse(data)[0] ?? null;
  },
});

const householdTeam: ReadonlySet<ViewerRole> = new Set(["owner", "manager", "staff"]);

// WHY: mirrors can_create for tasks: residents and vendors hold no assignment, so the server refuses their tasks and assignees.
export const canCreateTasks = (viewer: Viewer): boolean => householdTeam.has(viewer.role);

// WHY: mirrors private.search_scope (and the domain's canSearch): only firm owners and live assignments search; residents and vendors would only ever see "No matches" (slice 15 and the resident UI revisit this).
export const canSearch = (viewer: Pick<Viewer, "role">): boolean => householdTeam.has(viewer.role);

// WHY: projects belong to the household team (domain projectPermissions: residents, vendors and guests read nothing).
export const canBrowseProjects = (viewer: Pick<Viewer, "role">): boolean => householdTeam.has(viewer.role);

const contractTeam: ReadonlySet<ViewerRole> = new Set(["owner", "manager"]);

// WHY: contracts are confidential (27-contracts): owners and managers only, as can_access decides; staff and below get no item, chip or route.
export const canBrowseContracts = (viewer: Pick<Viewer, "role">): boolean => contractTeam.has(viewer.role);

// WHY: People is the household team's directory; residents and vendors never open a card, so nothing fetches or links one for them.
export const canBrowsePeople = (viewer: Viewer): boolean => householdTeam.has(viewer.role);

// WHY: planning is the household team's (29-staff-planning → Access): residents and vendors see no item, layer or route.
export const canSeePlanning = (viewer: Pick<Viewer, "role">): boolean => householdTeam.has(viewer.role);
